import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clarificationService from '../service/clarificationService.mjs';
import ClarificationDrawer from './ClarificationDrawer.mjs';
import { useIsStaff } from './ClarificationBell.mjs';
import { STATUS_LABELS, STATUS_PILL, CHANGED_EVENT } from '../util/clarificationText.mjs';

/**
 * The questions about one claim, at the top of it.
 *
 *   staff     "2 questions about this claim", linking to each open one, and -
 *             when the server says they may - a button to ask one. An admin
 *             asks the claimant; a reviewer asks the stage below them. This is
 *             where an admin who opened the claim from the inbox, the email or
 *             their dashboard sees that a reviewer is waiting on them.
 *   claimant  the questions an admin has put to them, with an Answer link.
 *             The server lists nothing else: what reviewers ask each other
 *             about the claim is not theirs to see.
 *
 * Renders nothing when there is nothing to show or ask, so a claim page with
 * no questions looks exactly as it did.
 */
const ClaimQuestionsBanner = ({ claimType, claimId }) => {
  const isStaff = useIsStaff();
  const [threads, setThreads] = useState([]);
  const [askable, setAskable] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!claimId) return undefined;
    let cancelled = false;
    const load = () => clarificationService.list({ claimType, claimId, status: 'unresolved' })
      .then((rows) => { if (!cancelled) setThreads(rows); })
      .catch(() => { /* a banner is a convenience; the page works without it */ });
    load();
    window.addEventListener(CHANGED_EVENT, load);
    return () => { cancelled = true; window.removeEventListener(CHANGED_EVENT, load); };
  }, [claimType, claimId]);

  // Only staff can ask, and only the server knows whether they may ask about
  // THIS claim (their own, a closed one, another region's are refused).
  useEffect(() => {
    if (!isStaff || !claimId) return undefined;
    let cancelled = false;
    clarificationService.askOptions({ claimType, claimId })
      .then((res) => { if (!cancelled) setAskable(res?.canAsk ? res : null); })
      .catch(() => { if (!cancelled) setAskable(null); });
    return () => { cancelled = true; };
  }, [isStaff, claimType, claimId]);

  if (!threads.length && !askable) return null;
  const forMe = threads.filter(t => t.needsMyAnswer).length;
  const onlyClaimant = askable?.options?.length > 0 && askable.options.every(o => o.value === 'claimant');

  const heading = isStaff
    ? `${threads.length} question${threads.length === 1 ? '' : 's'} about this claim`
    : (threads.length === 1 ? 'A question about your claim' : `${threads.length} questions about your claim`);

  return (
    <>
      <div className="no-print bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 max-sm:mb-4" role="status">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="text-sm font-semibold text-blue-900">
            {threads.length > 0 ? heading : 'No open questions about this claim'}
            {forMe > 0 && <span className="ml-1 font-normal">· {forMe} waiting on your answer</span>}
          </p>
          {askable && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red"
            >
              {onlyClaimant ? 'Ask the claimant a question' : 'Ask a question'}
            </button>
          )}
        </div>
        {!isStaff && threads.length > 0 && (
          <p className="mt-1 text-sm text-blue-900">
            Your claim stays where it is - nothing has been sent back. Answer in the conversation.
          </p>
        )}
        {threads.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {threads.slice(0, 3).map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${STATUS_PILL[t.status] || STATUS_PILL.closed}`}>
                  {STATUS_LABELS[t.status] || t.status}
                </span>
                <span className="min-w-0 flex-1 text-gray-800 break-words">
                  {t.askedByName}: {t.questionPreview}
                </span>
                <Link to={`/clarifications/${t.id}`} className="text-stanford-red font-medium hover:underline">
                  {t.needsMyAnswer ? 'Answer' : 'Open'} ›
                </Link>
              </li>
            ))}
          </ul>
        )}
        {threads.length > 3 && (
          <Link
            to={isStaff ? '/clarifications?tab=open' : '/clarifications'}
            className="mt-2 inline-block text-xs text-stanford-red hover:underline"
          >
            All {threads.length} questions ›
          </Link>
        )}
      </div>

      {/* Outside the status region: the drawer is a dialog, not an announcement. */}
      {drawerOpen && (
        <ClarificationDrawer
          title="Questions about this claim"
          subtitle={askable?.subjectLabel}
          claimType={claimType}
          claimId={claimId}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
};

export default ClaimQuestionsBanner;
